import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { MenuItem } from '../types';
import { migrationApi } from '../services/migrationApi';
import SuccessModal from './SuccessModal';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showCleanupModal, setShowCleanupModal] = useState<boolean>(false);
  const [cleanupMessage, setCleanupMessage] = useState<string>('');
  const [cleanupTitle, setCleanupTitle] = useState<string>('');
  const [isCleanupError, setIsCleanupError] = useState<boolean>(false);
  const [isCleaningUp, setIsCleaningUp] = useState<boolean>(false);
  const menuItems: MenuItem[] = [
    {
      id: 'migration',
      label: 'Migration',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19.35 10.04A7.49 7.49 0 0 0 12 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 0 0 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM14 13v4h-4v-4H7l5-5 5 5h-3z" fill="currentColor"/>
        </svg>
      )
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" fill="currentColor"/>
        </svg>
      )
    },
    {
      id: 'clear',
      label: 'Clear',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
        </svg>
      )
    }
  ];

  const handleCleanup = async (): Promise<void> => {
    setIsCleaningUp(true);
    try {
      const response = await migrationApi.cleanup();
      setCleanupTitle('Success');
      setCleanupMessage(response.description || 'Cleanup completed successfully');
      setIsCleanupError(false);
      setShowCleanupModal(true);
    } catch (error: any) {
      setCleanupTitle('Error');
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.description || error.message || 'Something went wrong during cleanup.';
      setCleanupMessage(errorMessage);
      setIsCleanupError(true);
      setShowCleanupModal(true);
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleCleanupModalClose = (): void => {
    setShowCleanupModal(false);
    setCleanupMessage('');
    setCleanupTitle('');
    setIsCleanupError(false);
  };

  const handleItemClick = (itemId: string): void => {
    if (itemId === 'migration') {
      navigate('/dashboard/migration');
    } else if (itemId === 'reports') {
      navigate('/dashboard/reports');
    } else if (itemId === 'clear') {
      handleCleanup();
    }
  };

  const isActive = (itemId: string): boolean => {
    const path = location.pathname;
    if (itemId === 'migration') {
      return path === '/dashboard' || path === '/dashboard/migration';
    }
    return path.includes(`/dashboard/${itemId}`);
  };

  return (
    <>
      <aside className="sidebar">
        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.id}
              className={`sidebar-item ${isActive(item.id) ? 'active' : ''}`}
              onClick={() => handleItemClick(item.id)}
              disabled={item.id === 'clear' && isCleaningUp}
            >
              <span className="sidebar-icon">{item.icon}</span>
              <span className="sidebar-label">
                {item.id === 'clear' && isCleaningUp ? 'Cleaning...' : item.label}
              </span>
            </button>
          ))}
        </nav>
      </aside>
      <SuccessModal
        isOpen={showCleanupModal}
        onClose={handleCleanupModalClose}
        title={cleanupTitle}
        message={cleanupMessage}
        variant={isCleanupError ? 'error' : 'success'}
        confirmText="OK"
      />
    </>
  );
};

export default Sidebar;
